import React, { useCallback, useState } from 'react';
import { createDefaultLot, type LotInput, TaxInfoStep } from './tax-info';
import { AddInvestmentCommonHeader } from '../../modals/add-investment-common-modal';
import { ModalContent, ModalFooter } from '../../modal';
import type { Portfolio } from 'venn-api';
import { AlertNote, Button } from 'venn-ui-kit';
import { useAllocationSearch } from './AllocationSearchContext';

interface AllocationSearchAddTaxInformationViewRedesignedProps {
  onClose: () => void;
  onAddTaxInformation: (selectedInvestmentId: string, lots: LotInput[]) => void;
  strategy: Portfolio;
}

export const AllocationSearchAddTaxInformationViewRedesigned: React.FC<
  AllocationSearchAddTaxInformationViewRedesignedProps
> = ({ onClose, onAddTaxInformation, strategy }) => {
  const [lots, setLots] = useState<LotInput[]>([createDefaultLot()]);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);
  const { selectedInvestmentId, handleBack } = useAllocationSearch();
  const handleSave = useCallback(() => {
    onAddTaxInformation(selectedInvestmentId!, lots);
    onClose();
  }, [selectedInvestmentId, lots, onAddTaxInformation, onClose]);

  const hasAtLeastOneLotFilled = lots.some((lot) => lot.marketValue !== 0 && lot.costBasis !== 0 && lot.quantity !== 0);

  const title = (
    <>
      <strong>Add investment to: </strong>
      {strategy.name}
    </>
  );

  return (
    <>
      <AddInvestmentCommonHeader title={title} onClose={onClose} />
      <ModalContent className="p-0 flex-1 min-h-0 overflow-hidden">
        <TaxInfoStep lots={lots} onLotsChange={setLots} setSaveDisabled={setIsSaveDisabled} />
      </ModalContent>
      <AlertNote size="small" className="justify-end">
        Lots added to this portfolio apply wherever this portfolio is used.
      </AlertNote>
      <ModalFooter
        className="!bg-venn-white mt-0 px-4 py-3 border-0 border-t border-solid border-venn-grey-30 h-16"
        simplifiedButtonStyles
        leftChildren={
          <Button refreshedStyling noMargin icon="chevron-left" onClick={handleBack}>
            Back
          </Button>
        }
        rightChildren={
          <Button refreshedStyling onClick={onClose}>
            Skip tax info and add selection
          </Button>
        }
        primaryLabel="Add Selection"
        onPrimaryClick={handleSave}
        primaryDisabled={!hasAtLeastOneLotFilled || isSaveDisabled}
      />
    </>
  );
};
